import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { 
  Mail, 
  Settings, 
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  Play,
  Pause,
  Loader2,
  RefreshCw,
  TestTube,
  MailOpen,
  Users,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Live countdown preview component
const CountdownPreview = ({ daysRemaining }) => {
  const [time, setTime] = useState({ days: daysRemaining, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    // Calculate end time based on days remaining
    const endTime = new Date();
    endTime.setDate(endTime.getDate() + daysRemaining);
    endTime.setHours(23, 59, 59, 0);
    
    const updateCountdown = () => {
      const now = new Date();
      const diff = endTime - now;
      
      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTime({ days, hours, minutes, seconds });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [daysRemaining]);
  
  return (
    <div className="flex justify-center gap-4 text-white">
      <div className="text-center">
        <div className="text-3xl font-bold">{time.days}</div>
        <div className="text-xs opacity-80">dage</div>
      </div>
      <div className="text-3xl font-bold">:</div>
      <div className="text-center">
        <div className="text-3xl font-bold">{String(time.hours).padStart(2, '0')}</div>
        <div className="text-xs opacity-80">timer</div>
      </div>
      <div className="text-3xl font-bold">:</div>
      <div className="text-center">
        <div className="text-3xl font-bold">{String(time.minutes).padStart(2, '0')}</div>
        <div className="text-xs opacity-80">min</div>
      </div>
      <div className="text-3xl font-bold">:</div>
      <div className="text-center">
        <div className="text-3xl font-bold">{String(time.seconds).padStart(2, '0')}</div>
        <div className="text-xs opacity-80">sek</div>
      </div>
    </div>
  );
};

const EmailFlowPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [flows, setFlows] = useState([]);
  const [activeTab, setActiveTab] = useState('settings');
  const [sendingTest, setSendingTest] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [startingTestFlow, setStartingTestFlow] = useState(false);
  const [markingConverted, setMarkingConverted] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, statsRes, flowsRes] = await Promise.all([
        axios.get(`${API}/admin/email-flow/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/email-flow/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/email-flow/flows?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setSettings(settingsRes.data);
      setStats(statsRes.data);
      setFlows(flowsRes.data);
    } catch (err) {
      console.error('Failed to fetch email flow data:', err);
      toast.error('Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/email-flow/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Indstillinger gemt');
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Kunne ikke gemme indstillinger');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async (emailId) => {
    if (!settings.test_email) {
      toast.error('Angiv en test-email først');
      return;
    }

    setSendingTest(emailId);
    try {
      await axios.post(`${API}/admin/email-flow/test-email`, {
        email_id: emailId,
        test_email: settings.test_email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Test-email sendt til ${settings.test_email}`);
    } catch (err) {
      console.error('Failed to send test email:', err);
      toast.error('Kunne ikke sende test-email');
    } finally {
      setSendingTest(null);
    }
  };

  const processPending = async () => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/admin/email-flow/process-pending`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.sent} emails sendt`);
      fetchData();
    } catch (err) {
      console.error('Failed to process pending:', err);
      toast.error('Kunne ikke behandle ventende emails');
    } finally {
      setProcessing(false);
    }
  };

  const startTestFlow = async () => {
    setStartingTestFlow(true);
    try {
      const res = await axios.post(`${API}/admin/email-flow/start-test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success(res.data.message);
        fetchData();
        setActiveTab('flows'); // Switch to flows tab to see the new test flow
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.error('Failed to start test flow:', err);
      toast.error(err.response?.data?.detail || 'Kunne ikke starte test flow');
    } finally {
      setStartingTestFlow(false);
    }
  };

  const markAsConverted = async (bookingId) => {
    setMarkingConverted(bookingId);
    try {
      await axios.post(`${API}/admin/email-flow/mark-converted/${bookingId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Flow markeret som konverteret');
      fetchData();
    } catch (err) {
      console.error('Failed to mark as converted:', err);
      toast.error('Kunne ikke markere som konverteret');
    } finally {
      setMarkingConverted(null);
    }
  };

  const updateEmail = (index, field, value) => {
    const newSettings = { ...settings };
    newSettings.emails[index][field] = value;
    setSettings(newSettings);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="h-8 w-8 text-teal-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Email Follow-up Flow</h1>
              <p className="text-slate-500">Automatisk opfølgning på tilbud der ikke er konverteret</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={startTestFlow} 
              disabled={startingTestFlow || !settings?.enabled || !settings?.test_mode}
              title={!settings?.enabled ? 'Aktivér flow først' : !settings?.test_mode ? 'Aktivér test-tilstand først' : 'Start et test flow'}
            >
              {startingTestFlow ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TestTube className="h-4 w-4 mr-2" />}
              Start test flow
            </Button>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Opdater
            </Button>
            <Button onClick={processPending} disabled={processing || !settings?.enabled}>
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send ventende
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                <Users className="h-4 w-4" />
                Aktive flows
              </div>
              <p className="text-3xl font-bold text-teal-600">
                {stats?.flow_statuses?.active || 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                <MailOpen className="h-4 w-4" />
                Emails sendt
              </div>
              <p className="text-3xl font-bold text-slate-700">
                {stats?.total_emails_sent || 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Konverteret
              </div>
              <p className="text-3xl font-bold text-green-600">
                {stats?.flow_statuses?.converted || 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                <Clock className="h-4 w-4" />
                Status
              </div>
              <div className="flex items-center gap-2">
                {settings?.enabled ? (
                  <>
                    <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse" />
                    <span className="font-medium text-green-600">Aktiv</span>
                  </>
                ) : (
                  <>
                    <div className="h-3 w-3 bg-slate-400 rounded-full" />
                    <span className="font-medium text-slate-500">Deaktiveret</span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200">
          <button
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'settings' 
                ? 'text-teal-600 border-b-2 border-teal-600' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings className="h-4 w-4 inline mr-2" />
            Indstillinger
          </button>
          <button
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'emails' 
                ? 'text-teal-600 border-b-2 border-teal-600' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('emails')}
          >
            <Mail className="h-4 w-4 inline mr-2" />
            Email-skabeloner
          </button>
          <button
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'flows' 
                ? 'text-teal-600 border-b-2 border-teal-600' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('flows')}
          >
            <Users className="h-4 w-4 inline mr-2" />
            Aktive flows ({flows.length})
          </button>
        </div>

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <Card>
            <CardHeader>
              <CardTitle>Generelle indstillinger</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Aktivér email flow</Label>
                  <p className="text-sm text-slate-500">Send automatisk opfølgningsmails til kunder</p>
                </div>
                <Switch
                  checked={settings?.enabled || false}
                  onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <Label className="text-base font-medium text-amber-800">Test-tilstand</Label>
                    <p className="text-sm text-amber-700">
                      Når aktiveret sendes alle emails til test-adressen i stedet for kunden
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings?.test_mode || false}
                  onCheckedChange={(checked) => setSettings({ ...settings, test_mode: checked })}
                />
              </div>

              <div>
                <Label htmlFor="testEmail">Test-email adresse</Label>
                <Input
                  id="testEmail"
                  type="email"
                  value={settings?.test_email || ''}
                  onChange={(e) => setSettings({ ...settings, test_email: e.target.value })}
                  placeholder="test@example.com"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Bruges til at modtage test-emails og i test-tilstand
                </p>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <Label className="text-base font-medium text-blue-800">Tilbuds gyldighed</Label>
                    <p className="text-sm text-blue-700 mb-3">
                      Hvor mange dage er et tilbud gyldigt? Bruges til nedtælling i emails.
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="60"
                        value={settings?.quote_valid_days || 10}
                        onChange={(e) => setSettings({ ...settings, quote_valid_days: parseInt(e.target.value) || 10 })}
                        className="w-20 bg-white"
                      />
                      <span className="text-sm text-blue-700">dage</span>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      Brug <code className="bg-blue-100 px-1 rounded">{'{days_remaining}'}</code> i emne og besked for at vise nedtælling
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button onClick={saveSettings} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Gem indstillinger
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Emails Tab */}
        {activeTab === 'emails' && (
          <div className="space-y-4">
            {settings?.emails?.map((email, index) => (
              <Card key={email.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-600 font-bold">
                        {index + 1}
                      </div>
                      {email.name}
                    </CardTitle>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">Aktiv</Label>
                        <Switch
                          checked={email.enabled}
                          onCheckedChange={(checked) => updateEmail(index, 'enabled', checked)}
                        />
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => sendTestEmail(email.id)}
                        disabled={sendingTest === email.id}
                      >
                        {sendingTest === email.id ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <TestTube className="h-4 w-4 mr-1" />
                        )}
                        Test
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Dage efter besøg</Label>
                      <Input
                        type="number"
                        min="1"
                        max="30"
                        value={email.days_after_visit}
                        onChange={(e) => updateEmail(index, 'days_after_visit', parseInt(e.target.value))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Dage tilbage på tilbud</Label>
                      <div className="mt-1 h-10 px-3 flex items-center bg-teal-50 border border-teal-200 rounded-md">
                        <Clock className="h-4 w-4 text-teal-600 mr-2" />
                        <span className="font-bold text-teal-700">
                          {Math.max(0, (settings?.quote_valid_days || 10) - email.days_after_visit)} dage
                        </span>
                      </div>
                    </div>
                    <div>
                      <Label>Emne</Label>
                      <Input
                        value={email.subject}
                        onChange={(e) => updateEmail(index, 'subject', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Besked</Label>
                    <Textarea
                      value={email.body}
                      onChange={(e) => updateEmail(index, 'body', e.target.value)}
                      rows={6}
                      className="mt-1 font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Brug <code className="bg-slate-100 px-1 rounded">{'{customer_name}'}</code> for kundens navn og <code className="bg-slate-100 px-1 rounded">{'{days_remaining}'}</code> for nedtælling
                    </p>
                  </div>
                  
                  {/* Live countdown preview */}
                  <div className="p-4 bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg">
                    <p className="text-white text-sm font-medium text-center mb-2">⏰ Live nedtæller i email (preview):</p>
                    <div className="text-center">
                      <CountdownPreview daysRemaining={Math.max(0, (settings?.quote_valid_days || 10) - email.days_after_visit)} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            <div className="pt-4">
              <Button onClick={saveSettings} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Gem alle ændringer
              </Button>
            </div>
          </div>
        )}

        {/* Flows Tab */}
        {activeTab === 'flows' && (
          <Card>
            <CardHeader>
              <CardTitle>Aktive email flows</CardTitle>
            </CardHeader>
            <CardContent>
              {flows.length === 0 ? (
                <p className="text-slate-500 text-center py-8">
                  Ingen aktive email flows endnu. 
                  <br />
                  Flows oprettes automatisk når kunder har tilbud der ikke er konverteret.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Kunde</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Email</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Besøgsdato</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600">Status</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600">Emails sendt</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600">Handlinger</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flows.map((flow) => {
                        const sentCount = flow.emails?.filter(e => e.status === 'sent').length || 0;
                        const totalCount = flow.emails?.length || 0;
                        
                        return (
                          <tr key={flow.id} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium">{flow.customer_name}</td>
                            <td className="py-3 px-4 text-slate-600">{flow.customer_email}</td>
                            <td className="py-3 px-4 text-slate-600">
                              {new Date(flow.visit_date).toLocaleDateString('da-DK')}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                flow.flow_status === 'active' ? 'bg-green-100 text-green-700' :
                                flow.flow_status === 'converted' ? 'bg-blue-100 text-blue-700' :
                                flow.flow_status === 'completed' ? 'bg-slate-100 text-slate-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {flow.flow_status === 'active' ? 'Aktiv' :
                                 flow.flow_status === 'converted' ? 'Konverteret' :
                                 flow.flow_status === 'completed' ? 'Fuldført' :
                                 'Stoppet'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {sentCount} / {totalCount}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {flow.flow_status === 'active' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => markAsConverted(flow.booking_id)}
                                  disabled={markingConverted === flow.booking_id}
                                >
                                  {markingConverted === flow.booking_id ? (
                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                  ) : (
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                  )}
                                  Konverteret
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EmailFlowPage;
